import { useEffect, useMemo, useState } from 'react';
import { X, Loader, Search, FileText, Unlink, ExternalLink } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface ReportRow {
  id: string;
  name: string | null;
  artist_id: string | null;
  platforms: string[] | null;
}

interface Props {
  releaseId: string;
  releaseName: string | null;
  artistName: string | null;
  artistId: string | null;
  currentReportId: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const REPORTING_SUPABASE_URL =
  (import.meta as any).env?.VITE_REPORTING_SUPABASE_URL || 'https://uydhsjvwrgupgfjevqsz.supabase.co';
const REPORTING_SUPABASE_ANON_KEY =
  (import.meta as any).env?.VITE_REPORTING_SUPABASE_ANON_KEY ||
  'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';

const reportingSupabase = createClient(REPORTING_SUPABASE_URL, REPORTING_SUPABASE_ANON_KEY);

export function LinkReleaseReportModal({
  releaseId,
  releaseName,
  artistName,
  artistId,
  currentReportId,
  onClose,
  onSaved,
}: Props) {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [currentReport, setCurrentReport] = useState<ReportRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Pull reports — prefer ones for this artist if we know the id.
        const sel = reportingSupabase.from('reports').select('id, name, artist_id, platforms').limit(500);
        const { data, error } = await sel;
        if (error) throw error;
        if (cancelled) return;
        const rows = (data as any[]) as ReportRow[];
        setReports(rows);
        if (currentReportId) {
          const cur = rows.find((r) => r.id === currentReportId) || null;
          setCurrentReport(cur);
        }
      } catch (err) {
        console.error('Failed to load reports:', err);
        toast.error('Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentReportId]);

  // Token-based scoring against report name. Higher score = better match.
  // Heuristics:
  //   +50  same artist_id as the current release
  //   +30  full release name appears as a substring
  //   +20  full artist name appears as a substring
  //   +5   per matching token (artist or release tokens, length >= 3)
  //   +25  manual search query is a substring (when typed)
  //   +5   per manual-query token match
  // Tokens are normalised to lowercase, accents stripped, non-alnum
  // collapsed. We also drop common filler tokens (the, a, &, feat, etc.)
  // so they don't inflate scores.
  const stop = useMemo(
    () => new Set(['the', 'a', 'an', 'of', 'and', 'or', 'feat', 'ft', 'with', 'to', 'in', 'on', 'by']),
    [],
  );
  const norm = (s: string | null | undefined) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const tokens = (s: string | null | undefined) =>
    norm(s)
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length >= 3 && !stop.has(t));

  const releaseTokens = useMemo(() => tokens(releaseName), [releaseName]);
  const artistTokens = useMemo(() => tokens(artistName), [artistName]);
  const releaseNorm = useMemo(() => norm(releaseName), [releaseName]);
  const artistNorm = useMemo(() => norm(artistName), [artistName]);

  const scoreReport = (r: ReportRow, q: string): number => {
    const name = norm(r.name);
    let score = 0;
    if (artistId && r.artist_id === artistId) score += 50;
    if (releaseNorm && name.includes(releaseNorm)) score += 30;
    if (artistNorm && name.includes(artistNorm)) score += 20;
    const reportTokens = new Set(name.split(/[^a-z0-9]+/));
    for (const t of releaseTokens) if (reportTokens.has(t)) score += 5;
    for (const t of artistTokens) if (reportTokens.has(t)) score += 5;
    if (q) {
      const qNorm = norm(q);
      if (name.includes(qNorm)) score += 25;
      for (const t of tokens(q)) if (reportTokens.has(t)) score += 5;
    }
    return score;
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim();
    const scored = reports.map((r) => ({ r, score: scoreReport(r, q) }));
    // When a search query is typed, restrict to results that actually
    // hit on something query-related (substring or any matching token).
    let list = scored;
    if (q) {
      const qNorm = norm(q);
      const qTokens = tokens(q);
      list = scored.filter(({ r }) => {
        const name = norm(r.name);
        if (name.includes(qNorm)) return true;
        if (qTokens.length === 0) return false;
        const reportTokens = new Set(name.split(/[^a-z0-9]+/));
        return qTokens.some((t) => reportTokens.has(t));
      });
    }
    list.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.r.name || '').localeCompare(b.r.name || '');
    });
    return list.slice(0, 100);
  }, [reports, searchQuery, artistId, releaseTokens, artistTokens, releaseNorm, artistNorm, stop]);

  // Top match: highest score AND not already linked. We treat anything
  // scoring >= 30 (substring of release/artist or same artist + tokens) as
  // a recommendation; that prevents the badge from showing for weak hits.
  const recommended = useMemo(() => {
    const top = filtered[0];
    if (!top) return null;
    if (top.score < 30) return null;
    if (top.r.id === currentReportId) return null;
    return top.r;
  }, [filtered, currentReportId]);

  const link = async (r: ReportRow) => {
    setSaving(r.id);
    try {
      const { error } = await (supabase as any)
        .from('release_campaign_links')
        .upsert(
          { release_id: releaseId, report_id: r.id },
          { onConflict: 'release_id' },
        );
      if (error) throw error;
      toast.success('Report linked');
      await onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to link report:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to link report');
    } finally {
      setSaving(null);
    }
  };

  const unlink = async () => {
    setUnlinking(true);
    try {
      const { error } = await (supabase as any)
        .from('release_campaign_links')
        .update({ report_id: null })
        .eq('release_id', releaseId);
      if (error) throw error;
      toast.success('Report unlinked');
      await onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to unlink report:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to unlink report');
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold dark:text-white flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-700 dark:text-gray-300" />
            Link reporting dashboard
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 overflow-y-auto flex-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Release:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {artistName || '?'} — {releaseName || '?'}
            </span>
          </p>

          {currentReportId && (
            <div className="mb-4 p-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Currently linked</div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm dark:text-white truncate flex items-center gap-1">
                  <ExternalLink className="h-3 w-3 shrink-0 text-gray-400" />
                  {currentReport?.name || currentReportId}
                </div>
                <button
                  onClick={unlink}
                  disabled={unlinking}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 disabled:opacity-50"
                >
                  {unlinking ? <Loader className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                  Unlink
                </button>
              </div>
              <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 break-all">id: {currentReportId}</div>
            </div>
          )}

          <div className="relative mb-2">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reports by name…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
              <Loader className="h-3 w-3 animate-spin" /> Loading reports…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">No reports found.</div>
          ) : (
            <ul className="mt-2 space-y-1 max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
              {filtered.map(({ r, score }) => {
                const isCurrent = r.id === currentReportId;
                const isMine = artistId && r.artist_id === artistId;
                const isRecommended = recommended?.id === r.id;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => link(r)}
                      disabled={saving === r.id || isCurrent}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-start justify-between gap-2 ${
                        isCurrent
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : isRecommended
                            ? 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      } disabled:opacity-50`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.name || '(unnamed)'}</div>
                        <div className="text-gray-500 dark:text-gray-400 truncate">
                          {(r.platforms || []).join(', ') || '—'}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isRecommended && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 dark:bg-amber-700/40 dark:text-amber-200 font-medium">
                            ★ Recommended
                          </span>
                        )}
                        {isMine && !isRecommended && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            this artist
                          </span>
                        )}
                        {isCurrent && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                            linked
                          </span>
                        )}
                        {score > 0 && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500" title={`match score: ${score}`}>
                            {score}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
